import BTButton from '@common-components/bt-button/bt-button'
import RowStack from '@common-components/common/row-stack/RowStack'
import Tooltip from '@mui/material/Tooltip'
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react'
import { closeBulkDashboard, setActiveBulkView, setActiveTab, resetCampaignAndTemplate, resetCampaignAndTemplateThunk } from '@common-reducers/CampaignReducer'
import { resetScheduledMessageEditor } from '@common-reducers/ScheduledMessageEditorReducer'
import { getIsCampaignWithoutContacts, selectCanContinueToNextTab, selectIsTimingValid } from '@common-reducers/CampaignSelectors'
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import BulkAudiencesList from './audiences/bulk-audiences-list'
import LaunchCampaignFlowTabs from './bt-bulk-launch-campaign-flow-tabs'
import BulkDBFinish from './finish/bulk-db-finish'
import BulkSetMessage from './message-and-files/bulk-set-message'
import NextTabButton from './next-tab-button'
import BulkSetTimeAndDate from './time-and-date/bulk-set-time-and-date'
import { launchCampaignThunk } from '@common-reducers/CampaignThunks'
import { getRuntimeUrl } from '@common-services/storage'
import { isExtensionContext } from "@common-services/detect-context"
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common-config/constants'
import CampaignInternalHeader from './campaign-internal-header'
import { useIsMobile } from '@common/hooks/use-is-mobile'

export default function BTBulkDashboard() {
    const dispatch = useAppDispatch()
    const { t, i18n } = useTranslation(namespace)
    const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';
    const isMobile = useIsMobile();

    const activeTab = useAppSelector((state) => state.CampaignReducer.activeTab);
    const canContinueToNextTab = useAppSelector((state) => selectCanContinueToNextTab(state));
    const isCampaignWithoutContacts = useAppSelector((state) => getIsCampaignWithoutContacts(state))
    const isTimingValid = useAppSelector((state) => selectIsTimingValid(state))
    const isCampaignLaunching = useAppSelector((state) => state.CampaignReducer.isCampaignLaunching)
    const selectedAudienceBoards = useAppSelector((state) => {
        const newCampaign = state.CampaignReducer.newCampaign;
        return newCampaign?.boardIDArray || [];
    });

    const isLaunchCampaignDisabled = isCampaignWithoutContacts || !isTimingValid || isCampaignLaunching;

    // Reason surfaced as a tooltip when the button is disabled. Without this,
    // the button silently no-ops — users have no way to tell why their click
    // did nothing (the cause of support #75, customer self-resolved after
    // trial-and-erroring his way through the requirements).
    const getLaunchDisabledReason = (): string => {
        if (isCampaignLaunching) {
            return t('campaign.launchDisabled.launching', { defaultValue: 'Launching your campaign…' });
        }
        if (isCampaignWithoutContacts) {
            return t('campaign.launchDisabled.noContacts', { defaultValue: 'Add at least one contact to your audience to launch this campaign.' });
        }
        if (!isTimingValid) {
            return t('campaign.launchDisabled.scheduleInPast', { defaultValue: 'The scheduled time is in the past — pick a future time or choose “Send now”.' });
        }
        return '';
    };
    const launchDisabledReason = getLaunchDisabledReason();

    const dispatchCloseBulkDashboard = () => {
        dispatch(closeBulkDashboard());
        dispatch(setActiveTab("manageCampaigns"))
        dispatch(setActiveBulkView("campaignManagementView"))
        dispatch(resetCampaignAndTemplateThunk());
    }

    const handleLaunchCampaignButtonClick = async () => {
        dispatch(launchCampaignThunk());
        dispatch(resetScheduledMessageEditor());
    };

    
    const srcFinishTab = getRuntimeUrl("/img/FinishTabWhite.svg")

    return (
        <div
            dir={isRtl ? 'rtl' : 'ltr'}
            className="flex flex-col h-[calc(100vh-81px)] w-full z-[1000] overflow-x-hidden overflow-y-auto"
        >
            {isExtensionContext() && (
                <CampaignInternalHeader
                    title={t('campaign.launchCampaign')}
                    onBackClick={dispatchCloseBulkDashboard}
                />
            )}
            <div className="flex flex-col h-full items-center w-full bg-background">
                <LaunchCampaignFlowTabs />
                <div className={cn(
                    "h-[calc(100vh-181px)] w-full flex justify-center items-start",
                    "overflow-y-auto overflow-x-hidden",

                    !isExtensionContext() && activeTab === 'messageAndFiles' && "pb-[200px]"
                )}>
                    {activeTab === 'audiences' && <BulkAudiencesList />}
                    {activeTab === 'timeAndDate' && <BulkSetTimeAndDate />}
                    {activeTab === 'messageAndFiles' && <BulkSetMessage />}
                    {activeTab === 'finish' && <BulkDBFinish />}
                </div>

                <div
                    className="fixed bottom-0 z-[1200] bg-[var(--campaign-footer-panel-bg)] dark:bg-[var(--campaign-footer-panel-bg)] border-t border-[var(--campaign-footer-panel-border)] dark:border-[var(--campaign-footer-panel-border)] shadow-[0px_-2px_4px_rgba(0,0,0,0.05)] w-screen px-3 box-border"
                    style={{ insetInlineStart: 0 }}
                >
                    <div className={cn(
                        "flex items-center py-2.5 h-[82px] box-border",
                        !isExtensionContext() ? "justify-between" : "justify-end",
                        (isExtensionContext() || isMobile) ? "w-full" : "w-full lg:w-[calc(100%-300px)]"
                    )}
                    style={{ marginInlineStart: 'auto' }}
                    >
                        {!isExtensionContext() && (
                            <Button 
                                variant="outline"
                                onClick={() => {
                                    dispatch(setActiveBulkView("campaignManagementView"))
                                    dispatch(setActiveTab("manageCampaigns"))
                                    dispatch(resetCampaignAndTemplate())
                                }}
                                className="justify-self-start"
                            >
                                {t('campaign.reset')}
                            </Button>
                        )}
                        {activeTab !== 'finish' && (
                            <NextTabButton isDisabled={!canContinueToNextTab} />
                        )}
                        {activeTab === 'finish' && (
                            <Tooltip
                                title={launchDisabledReason}
                                disableHoverListener={!isLaunchCampaignDisabled}
                                disableFocusListener={!isLaunchCampaignDisabled}
                                disableTouchListener={!isLaunchCampaignDisabled}
                                arrow
                            >
                                {/* MUI Tooltip needs a wrapper span when the child can be disabled */}
                                <span>
                                    <BTButton
                                        onClick={handleLaunchCampaignButtonClick}
                                        disabled={isLaunchCampaignDisabled}
                                        loading={isCampaignLaunching}
                                        sx={{
                                            minWidth: "250px",
                                            padding: "8px 16px",
                                            borderRadius: "8px",
                                            background: "linear-gradient(66.67deg, #259ae9 0%, #6dd1f1 100%)",
                                            "& img": {
                                                filter: "brightness(0) saturate(100%) invert(1) !important"
                                            },
                                            ".dark & img": {
                                                filter: "brightness(0) saturate(100%) invert(1) !important"
                                            }
                                        }}
                                    >
                                        {!isCampaignLaunching && (
                                            <img src={srcFinishTab} alt="Finish Tab"
                                                style={{
                                                    marginInlineEnd: "5px",
                                                    width: "25px",
                                                    height: "25px"
                                                }} />
                                        )}
                                        {t('campaign.launchCampaign')}
                                        {isRtl ? (
                                            <ChevronLeft className="ms-0.5 h-3.5 w-3.5 text-[var(--fab-text)]" />
                                        ) : (
                                            <ChevronRight className="ms-0.5 h-3.5 w-3.5 text-[var(--fab-text)]" />
                                        )}
                                    </BTButton>
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
